import { useState, useCallback } from "react";
import Confirmation from "../components/Confirmation";
import { GoogleOAuthProvider } from '@react-oauth/google';
import { trackEvent, getUtmParams } from '../utils/analytics';

const DemoCreatorWidget = () => {
  const [card, setCard] = useState('url');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [email, setEmail] = useState('');
  const [urlError, setUrlError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const csrfToken = document.querySelector("meta[name='csrf-token']").content;

  const isValidDomain = (url) => {
    const re = /([\da-z.-]+)\.([a-z.]{2,6})([/\w.-]*)*\/?$/i;
    return re.test(url) && !url.includes('@');
  };

  const handleUrlSubmit = () => {
    if (!isValidDomain(websiteUrl)) {
      setUrlError('Invalid domain. Please enter a valid URL');
      trackEvent('Form Error Displayed', {
        error_type: 'invalid_url',
        error_message: 'Invalid domain. Please enter a valid URL',
        field_name: 'website_url'
      }, { email, websiteUrl });
      return;
    }
    trackEvent('Website URL Entered', {}, { email, websiteUrl });
    trackEvent('Continue Button Clicked', {}, { email, websiteUrl });
    setUrlError('');
    setCard('email');
  };

  const startChatbotCreation = useCallback(async () => {
    setIsLoading(true);
    setSubmitError('');
    trackEvent('Chatbot Creation Started', {}, { email, websiteUrl });
    try {
      const res = await fetch('/stream-chatbot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken },
        body: JSON.stringify({ websiteUrl, email, utms: getUtmParams() }),
      });
      if (res.status === 429) {
        setSubmitError("You've reached the limit of 5 demo creations per hour. Please try again later.");
        setIsLoading(false);
        return;
      }
    } catch (e) {
      // network error — proceed to success, backend will retry
    }
    setIsLoading(false);
    setCard('success');
  }, [websiteUrl, email, csrfToken]);

  return (
    <>
      <div className="w-full max-w-[960px]">
        <div className="relative w-full flex flex-col items-start justify-center">
          <div className="ec-widget w-full flex flex-col items-center justify-center py-8 px-6 sm:px-10 md:px-16 gap-6">

            {card === 'url' && (
              <div className="ec-card">
                <h2 className="ec-headline">Paste URL. Create your AI.</h2>
                <div className="ec-input-row">
                  <input
                    type="url"
                    className="ec-input"
                    placeholder="Your website URL"
                    autoComplete="url"
                    value={websiteUrl}
                    onChange={(e) => { setWebsiteUrl(e.target.value); setUrlError(''); }}
                    onKeyDown={(e) => e.key === 'Enter' && handleUrlSubmit()}
                  />
                  <button className="ec-cta" onClick={handleUrlSubmit}>
                    Create my AI ›
                  </button>
                </div>
                {urlError && <p className="ec-error">{urlError}</p>}
                <p className="ec-url-subtext">
                  <svg width="14" height="14" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M2 7.5L6 11.5L13 4" stroke="#53d28c" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter"/>
                  </svg>
                  Free demo, ready in minutes.
                </p>
              </div>
            )}

            {card === 'email' && (
              <GoogleOAuthProvider clientId="513082693265-2igpm5ok74fpbedfh3kcv25ht2l81nt6.apps.googleusercontent.com">
                <Confirmation
                  email={email}
                  setEmail={setEmail}
                  startChatbotCreation={startChatbotCreation}
                  websiteUrl={websiteUrl}
                  isLoading={isLoading}
                  submitError={submitError}
                />
              </GoogleOAuthProvider>
            )}

            {card === 'success' && (
              <div className="ec-card">
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ margin: '0 auto 16px' }}>
                  <circle cx="20" cy="20" r="19" stroke="#53d28c" strokeWidth="2"/>
                  <path d="M11 20.5L17 26.5L29 14" stroke="#53d28c" strokeWidth="2" strokeLinecap="square"/>
                </svg>
                <h2 className="ec-headline">Your AI is being built!</h2>
                <p className="ec-subheadline">Check your inbox for your demo link.</p>
                <div className="ec-divider"><span>Or</span></div>
                <a
                  className="ec-status-btn"
                  href={`/status-checker/${encodeURIComponent(email)}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Check build status ›
                </a>
              </div>
            )}


          </div>
        </div>
      </div>
    </>
  );
};

export default DemoCreatorWidget;
